import React, { useEffect, useMemo, useState } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    AlertTriangle, Download, Filter, RefreshCw, Search, Calendar,
    Shuffle, UserCog, FileSpreadsheet, UploadCloud, ArrowRightLeft,
} from 'lucide-react';
import logger from '@/lib/logger';

const ALLOWED_ROLES = ['sales_manager', 'admin', 'super_admin', 'ceo', 'team_leader'];

const PERIOD_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom' },
];

const SOURCE_OPTIONS = [
    { value: '', label: 'All sources' },
    { value: 'round_robin', label: 'Round-robin (pool)' },
    { value: 'manual', label: 'Manual reassign' },
    { value: 'csv_import', label: 'CSV import' },
    { value: 'google_sheet', label: 'Google Sheet sync' },
];

const ACTION_OPTIONS = [
    { value: '', label: 'All actions' },
    { value: 'assigned_from_pool', label: 'Assigned from pool' },
    { value: 'bulk_assigned_from_pool', label: 'Bulk assigned' },
    { value: 'reassigned_via_erp', label: 'Reassigned by user' },
    { value: 'reassigned_via_sheet_sync', label: 'Reassigned by sheet' },
    { value: 'assigned_from_sheet_sync', label: 'New from sheet' },
    { value: 'imported_round_robin', label: 'CSV round-robin' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'scheduled_self_heal', label: 'Self-heal' },
    { value: 'revived_from_rejected', label: 'Revived from rejected' },
    { value: 'transferred', label: 'Transferred (dual approval)' },
];

const SOURCE_STYLES = {
    round_robin: { className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Shuffle, label: 'Round-Robin' },
    manual: { className: 'bg-purple-100 text-purple-800 border-purple-200', icon: UserCog, label: 'Manual' },
    csv_import: { className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: UploadCloud, label: 'CSV Import' },
    google_sheet: { className: 'bg-amber-100 text-amber-800 border-amber-200', icon: FileSpreadsheet, label: 'Sheet Sync' },
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

export default function LeadAssignmentAuditPage() {
    const { user } = useAuth();
    const isAllowed = ALLOWED_ROLES.includes(user?.role);

    const [period, setPeriod] = useState('today');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [teamId, setTeamId] = useState('');
    const [source, setSource] = useState('');
    const [action, setAction] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);

    const [teams, setTeams] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Load team list once
    useEffect(() => {
        if (!isAllowed) return;
        (async () => {
            try {
                const res = await apiClient.get('/teams?department=Sales');
                setTeams(res.data || []);
            } catch (e) { logger.error('teams load fail', e); }
        })();
    }, [isAllowed]);

    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        p.set('period', period);
        if (period === 'custom') {
            if (customStart) p.set('start_date', new Date(customStart).toISOString());
            if (customEnd) p.set('end_date', new Date(customEnd).toISOString());
        }
        if (teamId) p.set('team_id', teamId);
        if (source) p.set('source', source);
        if (action) p.set('action', action);
        if (search.trim()) p.set('search', search.trim());
        p.set('page', String(page));
        p.set('page_size', String(pageSize));
        return p;
    }, [period, customStart, customEnd, teamId, source, action, search, page, pageSize]);

    const fetchAudit = async () => {
        if (!isAllowed) return;
        setLoading(true);
        try {
            const res = await apiClient.get(`/sales/lead-assignment-audit?${queryParams.toString()}`);
            setData(res.data);
        } catch (e) {
            logger.error('audit load fail', e);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (period === 'custom' && (!customStart || !customEnd)) return;
        fetchAudit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, customStart, customEnd, teamId, source, action, page]);

    const handleApplySearch = (e) => {
        e?.preventDefault?.();
        setPage(1);
        fetchAudit();
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const resp = await apiClient.get(
                `/sales/lead-assignment-audit/export?${queryParams.toString()}`,
                { responseType: 'blob' },
            );
            const blob = new Blob([resp.data], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lead_assignment_audit_${period}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            logger.error('export fail', e);
        } finally {
            setExportLoading(false);
        }
    };

    if (!isAllowed) {
        return (
            <div className="p-6">
                <div className="max-w-md mx-auto mt-12 rounded-lg border bg-card p-6 text-center" data-testid="lead-audit-no-access">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                    <h2 className="text-lg font-semibold">Assignment Audit is manager-only</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        This view is restricted to Sales Managers, Team Leaders, CEO and Admins.
                    </p>
                </div>
            </div>
        );
    }

    const summary = data?.summary || {};
    const rows = data?.rows || [];
    const totalPages = Math.max(1, Math.ceil((data?.total || 0) / pageSize));

    return (
        <div className="p-6 space-y-5" data-testid="lead-assignment-audit-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
                        <ArrowRightLeft className="h-5 w-5 text-indigo-500" />
                        Lead Assignment Audit
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Every lead assignment — round-robin, manual reassign, CSV import,
                        Google Sheet sync, transfers and rejections — captured in one
                        searchable timeline.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={handleExport}
                    disabled={exportLoading || !data?.total}
                    data-testid="lead-audit-export-btn"
                >
                    <Download className="h-4 w-4 mr-2" />
                    {exportLoading ? 'Preparing…' : 'Export CSV'}
                </Button>
            </div>

            {/* Filters */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm inline-flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" /> Filters
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                        {/* Period */}
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                            <Select value={period} onValueChange={(v) => { setPeriod(v); setPage(1); }}>
                                <SelectTrigger data-testid="lead-audit-period-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PERIOD_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Team */}
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Team</label>
                            <Select
                                value={teamId || 'all'}
                                onValueChange={(v) => { setTeamId(v === 'all' ? '' : v); setPage(1); }}
                                disabled={user?.role === 'team_leader'}
                            >
                                <SelectTrigger data-testid="lead-audit-team-select">
                                    <SelectValue placeholder="All teams" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All teams</SelectItem>
                                    {teams.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Source */}
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Source</label>
                            <Select
                                value={source || 'all'}
                                onValueChange={(v) => { setSource(v === 'all' ? '' : v); setPage(1); }}
                            >
                                <SelectTrigger data-testid="lead-audit-source-select">
                                    <SelectValue placeholder="All sources" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SOURCE_OPTIONS.map((o) => (
                                        <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Action */}
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Action</label>
                            <Select
                                value={action || 'all'}
                                onValueChange={(v) => { setAction(v === 'all' ? '' : v); setPage(1); }}
                            >
                                <SelectTrigger data-testid="lead-audit-action-select">
                                    <SelectValue placeholder="All actions" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ACTION_OPTIONS.map((o) => (
                                        <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Search */}
                        <form onSubmit={handleApplySearch}>
                            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Lead, phone, agent…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    data-testid="lead-audit-search-input"
                                />
                                <Button type="submit" variant="outline" size="icon" data-testid="lead-audit-search-btn">
                                    <Search className="h-4 w-4" />
                                </Button>
                            </div>
                        </form>
                    </div>

                    {period === 'custom' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t">
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> Start date
                                </label>
                                <Input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => { setCustomStart(e.target.value); setPage(1); }}
                                    data-testid="lead-audit-custom-start"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> End date
                                </label>
                                <Input
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }}
                                    data-testid="lead-audit-custom-end"
                                />
                            </div>
                        </div>
                    )}

                    {data && (
                        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
                            <span data-testid="lead-audit-range-label">
                                Showing events between{' '}
                                <span className="font-mono">{fmtDate(data.start_date)}</span> and{' '}
                                <span className="font-mono">{fmtDate(data.end_date)}</span>
                            </span>
                            <Button variant="ghost" size="sm" onClick={fetchAudit} disabled={loading} data-testid="lead-audit-refresh-btn">
                                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card data-testid="lead-audit-card-total">
                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-muted-foreground">Total events</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">{(data?.total ?? 0).toLocaleString()}</div>
                    </CardContent>
                </Card>
                {['round_robin', 'manual', 'google_sheet'].map((src) => {
                    const meta = SOURCE_STYLES[src];
                    const count = (summary.by_source || []).find(s => s.source === src)?.count || 0;
                    const Icon = meta?.icon;
                    return (
                        <Card key={src} data-testid={`lead-audit-card-${src}`}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-xs uppercase text-muted-foreground inline-flex items-center gap-1">
                                    {Icon && <Icon className="h-3 w-3" />} {meta?.label || src}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold">{count.toLocaleString()}</div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Team breakdown + Top agents — side by side on lg */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by team</CardTitle></CardHeader>
                    <CardContent>
                        {(summary.by_team || []).filter(t => t.team_name).length === 0 ? (
                            <div className="text-xs text-muted-foreground py-3">No team-tagged events in range.</div>
                        ) : (
                            <div className="space-y-1.5">
                                {(summary.by_team || []).filter(t => t.team_name).slice(0, 8).map((t) => (
                                    <div key={t.team_name} className="flex items-center justify-between text-sm" data-testid={`lead-audit-team-row-${t.team_name}`}>
                                        <span>{t.team_name}</span>
                                        <Badge variant="secondary">{t.count}</Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Top assignees</CardTitle></CardHeader>
                    <CardContent>
                        {(summary.by_agent || []).length === 0 ? (
                            <div className="text-xs text-muted-foreground py-3">No agent events in range.</div>
                        ) : (
                            <div className="space-y-1.5">
                                {(summary.by_agent || []).slice(0, 8).map((a) => (
                                    <div key={a.agent_id} className="flex items-center justify-between text-sm">
                                        <span className="truncate">{a.agent_name}</span>
                                        <Badge variant="secondary">{a.count}</Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Events table */}
            <Card>
                <CardHeader className="pb-2 flex items-center justify-between flex-row">
                    <CardTitle className="text-sm">Events</CardTitle>
                    <div className="text-xs text-muted-foreground">
                        Page {page} of {totalPages} • {data?.total?.toLocaleString() || 0} total
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="whitespace-nowrap">When</TableHead>
                                    <TableHead>Lead</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>To agent</TableHead>
                                    <TableHead>Team</TableHead>
                                    <TableHead>From agent</TableHead>
                                    <TableHead>By</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                                            <RefreshCw className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                                            No assignment events match these filters.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && rows.map((r, idx) => {
                                    const meta = SOURCE_STYLES[r.source] || {};
                                    const Icon = meta.icon;
                                    return (
                                        <TableRow key={`${r.lead_id}-${r.date}-${idx}`} data-testid={`lead-audit-row-${idx}`}>
                                            <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.date)}</TableCell>
                                            <TableCell className="max-w-[200px]">
                                                <div className="font-medium truncate">{r.lead_name}</div>
                                                <div className="text-xs text-muted-foreground font-mono truncate">{r.lead_phone}</div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-xs">{(r.action || '').replace(/_/g, ' ')}</Badge>
                                                {r.reason && (
                                                    <div className="text-[10px] text-muted-foreground mt-0.5">{r.reason}</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.source ? (
                                                    <Badge className={`${meta.className || ''} text-xs inline-flex items-center gap-1`} variant="outline">
                                                        {Icon && <Icon className="h-3 w-3" />}
                                                        {meta.label || r.source}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm">{r.agent_name || '—'}</TableCell>
                                            <TableCell className="text-sm">{r.team_name || '—'}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{r.from_agent_name || '—'}</TableCell>
                                            <TableCell className="text-sm">
                                                <div>{r.assigned_by || '—'}</div>
                                                {r.by_user_role && (
                                                    <div className="text-[10px] text-muted-foreground">{r.by_user_role.replace(/_/g, ' ')}</div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-end gap-2 p-3 border-t">
                        <Button
                            variant="outline" size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            data-testid="lead-audit-prev-btn"
                        >Previous</Button>
                        <Button
                            variant="outline" size="sm"
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            data-testid="lead-audit-next-btn"
                        >Next</Button>
                    </div>
                )}
            </Card>
        </div>
    );
}
